import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { NotificationCategory, UserNotificationPreferenceModel } from '../../models/user-notification-preference.model';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ValueChangedEvent } from 'devextreme/ui/slider';

@Component({
  selector: 'sp-user-settings-notification-account',
  standalone: false,
  templateUrl: './user-settings-notification-account.component.html',
  styleUrl: './user-settings-notification-account.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class UserSettingsNotificationAccountComponent implements OnInit, OnChanges {

  @Input() userPreferences: UserNotificationPreferenceModel;
  @Output() updatePreferences = new EventEmitter<UserNotificationPreferenceModel>();

  formGroup: FormGroup;
  localUserPreferences: UserNotificationPreferenceModel;
  notifyDaysOptions: Array<{ text: string; value: number }> = [];

  constructor(private fb: FormBuilder) {
    this.initializeNotifyDaysOptions();
  }

  ngOnInit(): void {
    this.initForm();
    if (this.userPreferences) {
      this.localUserPreferences = this.userPreferences;
      this.updateFormValues();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.userPreferences?.currentValue) {
      this.localUserPreferences = changes.userPreferences.currentValue;
      this.updateFormValues();
    }
  }

  get isEmailDisabled(): boolean {
    return !this.userPreferences?.channelSetting?.email;
  }

  onCdMaturingSwitchChanged(e: ValueChangedEvent): void {
    if (!e.event || !this.localUserPreferences) {
      return;
    }

    if (this.isEmailDisabled) {
      this.updateFormValues();
      return;
    }

    const emailValue = this.formGroup.controls['cdMaturingSwitch'].value;
    const accountReminder = this.findOrCreateAccountReminder();
    
    accountReminder.email = emailValue;
    this.updatePreferences.emit(this.localUserPreferences); // <-- ensure this line is present
  }

  onNotifyDaysChanged(e: any): void {
    if (!e.event && e.value === undefined) {
      return;
    }

    if (!this.localUserPreferences) {
      return;
    }

    const selectedDays = e.value !== undefined 
      ? e.value 
      : this.formGroup.controls['notifyDaysDropdown'].value;

    const accountReminder = this.findOrCreateAccountReminder();
    accountReminder.notifyDays = selectedDays || 30;

    this.updatePreferences.emit(this.localUserPreferences); // <-- add this back
  }

  private findOrCreateAccountReminder() {
    if (!this.localUserPreferences.accountSettings) {
      this.localUserPreferences.accountSettings = [];
    }

    let accountReminder = this.localUserPreferences.accountSettings.find(
      x => x.notificationCategoryId === NotificationCategory.AccountReminder
    );

    if (!accountReminder) {
      const currentEmail = this.formGroup?.controls['cdMaturingSwitch']?.value ?? true;
      accountReminder = {
        notificationCategoryId: NotificationCategory.AccountReminder,
        email: currentEmail,
        notifyDays: 30
      };
      this.localUserPreferences.accountSettings.push(accountReminder);
    }

    return accountReminder;
  }

  private updateFormValues(): void {
    if (!this.formGroup || !this.localUserPreferences) {
      return;
    }

    const emailDisabled = !this.localUserPreferences.channelSetting?.email;
    const accountReminder = this.localUserPreferences.accountSettings?.find(
      x => x.notificationCategoryId === NotificationCategory.AccountReminder
    );

    const cdMaturingEmail = emailDisabled
      ? false
      : (accountReminder?.email ?? true);

    const notifyDays = accountReminder?.notifyDays ?? 30;

    this.formGroup.controls['cdMaturingSwitch'].setValue(cdMaturingEmail, { emitEvent: false });
    this.formGroup.controls['notifyDaysDropdown'].setValue(notifyDays, { emitEvent: false });

    const notifyCtrl = this.formGroup.get('notifyDaysDropdown');
    const shouldDisableNotify = emailDisabled || !cdMaturingEmail;
    if (shouldDisableNotify) {
      notifyCtrl?.disable({ emitEvent: false });
    } else {
      notifyCtrl?.enable({ emitEvent: false });
    }
  }

  private initializeNotifyDaysOptions(): void {
    for (let i = 1; i <= 60; i++) {
      const label = i === 1 ? '1 day' : `${i} days`;
      this.notifyDaysOptions.push({
        text: `${label} before maturity date`,
        value: i
      });
    }
  }

  private initForm(): void {
    this.formGroup = this.fb.group({
      cdMaturingSwitch: [''],
      notifyDaysDropdown: [30],
    });
  }
}